/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';
import { InstagramReel } from '../types';
import { useTranslation } from '../i18n/useTranslation';
import { CDN_URLS } from '../config';
import { getImageUrl, getAvatarUrl } from '../utils/imageUtils';

interface SocialCarouselProps {
    reels: InstagramReel[];
    title: string;
    icon: React.ReactNode;
}

const variants = {
    enter: (direction: number) => ({
        x: direction > 0 ? 300 : -300,
        opacity: 0,
        scale: 0.8,
    }),
    center: {
        zIndex: 1,
        x: 0,
        opacity: 1,
        scale: 1,
    },
    exit: (direction: number) => ({
        zIndex: 0,
        x: direction < 0 ? 300 : -300,
        opacity: 0,
        scale: 0.8,
    })
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
};

const SocialCarousel: React.FC<SocialCarouselProps> = ({ reels, title, icon }) => {
    const { t } = useTranslation();
    const [[page, direction], setPage] = useState([0, 0]);

    // This ensures the index is always within the bounds of the array
    const reelIndex = ((page % reels.length) + reels.length) % reels.length;

    const paginate = (newDirection: number) => {
        setPage([page + newDirection, newDirection]);
    };

    const currentReel = reels[reelIndex];


    return (
        <div>
            <h3 className="text-2xl font-serif font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                {icon} {title}
            </h3>
            <div className="relative aspect-[9/16] w-full max-w-sm mx-auto bg-gray-200 dark:bg-gray-700 rounded-2xl overflow-hidden shadow-lg select-none">
                <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                        key={page}
                        className="absolute w-full h-full cursor-grab active:cursor-grabbing"
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                            x: { type: "spring", stiffness: 300, damping: 30 },
                            opacity: { duration: 0.2 }
                        }}
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={1}
                        onDragEnd={(e, { offset, velocity }) => {
                            const swipe = swipePower(offset.x, velocity.x);
                            if (swipe < -swipeConfidenceThreshold) {
                                paginate(1);
                            } else if (swipe > swipeConfidenceThreshold) {
                                paginate(-1);
                            }
                        }}
                    >
                        <img
                            src={getImageUrl(currentReel.thumbnailUrl)}
                            alt={currentReel.captionKey ? t(currentReel.captionKey) : currentReel.caption}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                        />
                        <a href={currentReel.videoUrl} target="_blank" rel="noopener noreferrer" className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></a>
                        <div className="absolute bottom-0 left-0 p-4 text-white pointer-events-none">
                            <div className="flex items-center gap-2 mb-2">
                                <img
                                    src={getAvatarUrl(currentReel.avatarUrl)}
                                    alt={currentReel.author}
                                    className="w-8 h-8 rounded-full border-2 border-white"
                                    loading="lazy"
                                    decoding="async"
                                />
                                <span className="font-semibold text-sm drop-shadow-md">{currentReel.author}</span>
                            </div>
                            <p className="text-xs line-clamp-2 drop-shadow-sm">{currentReel.captionKey ? t(currentReel.captionKey) : currentReel.caption}</p>
                        </div>
                    </motion.div>
                </AnimatePresence>
                <button onClick={() => paginate(-1)} className="absolute top-1/2 left-2 transform -translate-y-1/2 z-10 bg-white/30 text-white p-1 rounded-full hover:bg-white/50 transition-colors backdrop-blur-sm" aria-label="Previous Reel">
                    <ChevronLeftIcon className="w-6 h-6" />
                </button>
                <button onClick={() => paginate(1)} className="absolute top-1/2 right-2 transform -translate-y-1/2 z-10 bg-white/30 text-white p-1 rounded-full hover:bg-white/50 transition-colors backdrop-blur-sm" aria-label="Next Reel">
                    <ChevronRightIcon className="w-6 h-6" />
                </button>
            </div>
            <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4 max-w-sm mx-auto">
                {t('socialCarousel_simulation_note')}
            </p>
        </div>
    );
};

export default SocialCarousel;